/**
 * Lineage for laptops (release 2.1 item 15): where each data type lives.
 *
 * Fleet default: a heatmap of business units x data types (never a hairball).
 * Graph mode: two columns, business units (or laptops, or one laptop) on the
 * left and data types on the right, each link carrying a count; a laptop adds
 * a third column with its top folders per type. The server caps the graph at
 * 150 nodes and folds the rest into "N others" nodes, so the browser never
 * draws a file-level graph. Click a business unit or a laptop to drill in.
 *
 * Layout is deterministic (columns, server order): no force layout, so the
 * same data always draws the same picture.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, GitBranch, Grid3x3, RefreshCw } from 'lucide-react';
import { ErrorState, LoadingState } from '@foctta/ui-shell';

import { GlassCard } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import {
  getFleetHeatmap,
  getFleetLineage,
  type LineageGraph,
  type LineageHeatmap,
  type LineageNode,
} from '@/lib/fleet-api';

import { FreshnessNote } from './FreshnessNote';
import { useLatestGuard } from './use-latest';
import { fmtCount, plural } from './fleet-format';

type Metric = 'files' | 'findings';

interface Crumb {
  scope: string;
  label: string;
}

const NODE_H = 22;
const GAP = 6;
const COL_W = 210;
const COL_X = [0, 360, 700] as const;

const NODE_FILL: Record<LineageNode['kind'], string> = {
  bu: 'hsl(var(--chart-2) / 0.25)',
  device: 'hsl(var(--chart-2) / 0.25)',
  type: 'hsl(var(--chart-1) / 0.22)',
  folder: 'hsl(var(--chart-6) / 0.22)',
  others: 'hsl(var(--muted))',
};

export function LineageGraphView({
  graph,
  onDrill,
}: {
  graph: LineageGraph;
  onDrill: (scope: string, label: string) => void;
}) {
  const layout = useMemo(() => {
    const cols: LineageNode[][] = [[], [], []];
    for (const n of graph.nodes) cols[n.column]!.push(n);
    const pos = new Map<string, { x: number; y: number }>();
    cols.forEach((col, ci) => col.forEach((n, i) => pos.set(n.id, { x: COL_X[ci]!, y: i * (NODE_H + GAP) })));
    const rows = Math.max(1, ...cols.map((c) => c.length));
    const usedCols = cols.filter((c) => c.length > 0).length;
    const maxEdge = Math.max(1, ...graph.edges.map((e) => e.value));
    return { pos, height: rows * (NODE_H + GAP), width: COL_X[Math.max(0, usedCols - 1)]! + COL_W, maxEdge };
  }, [graph]);

  return (
    <div className="overflow-auto" style={{ maxHeight: 640 }}>
      <svg
        width={layout.width + 8}
        height={layout.height + 8}
        viewBox={`-4 -4 ${layout.width + 8} ${layout.height + 8}`}
        role="img"
        aria-label="Where data types sit on laptops"
      >
        {graph.edges.map((e) => {
          const a = layout.pos.get(e.source);
          const b = layout.pos.get(e.target);
          if (!a || !b) return null;
          const x1 = a.x + COL_W;
          const y1 = a.y + NODE_H / 2;
          const x2 = b.x;
          const y2 = b.y + NODE_H / 2;
          const mid = (x1 + x2) / 2;
          return (
            <path
              key={`${e.source}>${e.target}`}
              d={`M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`}
              fill="none"
              stroke="hsl(var(--chart-2))"
              strokeOpacity={0.35}
              strokeWidth={1 + 7 * (e.value / layout.maxEdge)}
            >
              <title>{`${fmtCount(e.value)} ${graph.metric}`}</title>
            </path>
          );
        })}
        {graph.nodes.map((n) => {
          const p = layout.pos.get(n.id)!;
          const clickable = Boolean(n.drill);
          return (
            <g
              key={n.id}
              transform={`translate(${p.x},${p.y})`}
              onClick={clickable ? () => onDrill(n.drill!, n.label) : undefined}
              style={{ cursor: clickable ? 'pointer' : 'default' }}
            >
              <rect
                width={COL_W}
                height={NODE_H}
                rx={4}
                fill={NODE_FILL[n.kind]}
                stroke="hsl(var(--border))"
                strokeDasharray={n.kind === 'others' ? '3 3' : undefined}
              />
              <text x={8} y={15} fontSize={11} fill="hsl(var(--foreground))">
                {n.label.length > 26 ? `${n.label.slice(0, 25)}...` : n.label.replace(/_/g, ' ')}
              </text>
              <text x={COL_W - 6} y={15} fontSize={10} textAnchor="end" fill="hsl(var(--muted-foreground))">
                {fmtCount(n.value)}
              </text>
              <title>{`${n.label}: ${fmtCount(n.value)} ${graph.metric}${clickable ? ' (click to open)' : ''}`}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function HeatmapView({ heatmap, onDrill }: { heatmap: LineageHeatmap; onDrill: (scope: string, label: string) => void }) {
  const max = Math.max(1, ...heatmap.cells.flat());
  return (
    <div className="overflow-auto" style={{ maxHeight: 640 }}>
      <table className="text-[11px] border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th className="sticky left-0 bg-card text-left pr-3 font-medium text-muted-foreground">Business unit</th>
            {heatmap.columns.map((c) => (
              <th key={c.dataType} className="px-1 font-medium text-muted-foreground align-bottom" title={`${c.dataType}: ${fmtCount(c.total)}`}>
                <div className="w-14 truncate">{c.dataType.replace(/_/g, ' ')}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {heatmap.rows.map((r, ri) => (
            <tr key={`${r.id ?? 'u'}-${ri}`}>
              <th className="sticky left-0 bg-card text-left pr-3 font-normal whitespace-nowrap">
                {r.drill ? (
                  <button type="button" className="text-primary hover:underline" onClick={() => onDrill(r.drill!, r.label)}>
                    {r.label}
                  </button>
                ) : (
                  <span className="text-muted-foreground">{r.label}</span>
                )}
              </th>
              {heatmap.cells[ri]!.map((v, ci) => (
                <td
                  key={ci}
                  className="w-14 h-7 text-center tabular-nums rounded"
                  style={{ background: v > 0 ? `hsl(var(--chart-1) / ${0.12 + 0.78 * (v / max)})` : 'hsl(var(--muted) / 0.4)' }}
                  title={`${r.label} / ${heatmap.columns[ci]?.dataType}: ${fmtCount(v)}`}
                >
                  {v > 0 ? fmtCount(v) : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LaptopLineage() {
  const [metric, setMetric] = useState<Metric>('files');
  const [mode, setMode] = useState<'heatmap' | 'graph'>('heatmap');
  const [trail, setTrail] = useState<Crumb[]>([{ scope: 'fleet', label: 'All business units' }]);
  const [graph, setGraph] = useState<LineageGraph | null>(null);
  const [heatmap, setHeatmap] = useState<LineageHeatmap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const scope = trail[trail.length - 1]!.scope;
  const atFleet = scope === 'fleet';
  const showHeatmap = atFleet && mode === 'heatmap';

  const begin = useLatestGuard();
  const load = useCallback(async () => {
    const current = begin();
    setLoading(true);
    setError(null);
    try {
      // A drill, a metric switch or a heatmap/graph switch can start a new
      // request before this one answers; only the latest may land.
      if (showHeatmap) {
        const h = await getFleetHeatmap({ metric });
        if (current()) setHeatmap(h);
      } else {
        const g = await getFleetLineage({ scope, metric, maxNodes: 150 });
        if (current()) setGraph(g);
      }
    } catch (e) {
      if (current()) setError(e instanceof Error ? e.message : 'Could not load laptop lineage');
    } finally {
      if (current()) setLoading(false);
    }
  }, [showHeatmap, metric, scope, begin]);

  useEffect(() => {
    void load();
  }, [load]);

  const drill = (next: string, label: string) => {
    setTrail((t) => [...t, { scope: next, label }]);
    setMode('graph');
  };

  const current = showHeatmap ? heatmap : graph;
  const empty = showHeatmap ? heatmap !== null && heatmap.rows.length === 0 : graph !== null && graph.nodes.length === 0;

  return (
    <GlassCard className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <nav className="flex items-center gap-1 text-xs" aria-label="Lineage scope">
          {trail.map((c, i) => (
            <span key={`${c.scope}-${i}`} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
              {i < trail.length - 1 ? (
                <button type="button" className="text-primary hover:underline" onClick={() => setTrail((t) => t.slice(0, i + 1))}>
                  {c.label}
                </button>
              ) : (
                <span className="font-medium">{c.label}</span>
              )}
            </span>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {atFleet && (
            <div className="inline-flex rounded-md glass-lite p-0.5 gap-0.5">
              {(['heatmap', 'graph'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 rounded text-[11px]',
                    mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/40',
                  )}
                >
                  {m === 'heatmap' ? <Grid3x3 className="h-3 w-3" /> : <GitBranch className="h-3 w-3" />}
                  {m === 'heatmap' ? 'Heatmap' : 'Graph'}
                </button>
              ))}
            </div>
          )}
          <select
            aria-label="Count"
            value={metric}
            onChange={(e) => setMetric(e.target.value as Metric)}
            className="glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40"
          >
            <option value="files">Count files</option>
            <option value="findings">Count findings</option>
          </select>
          <Button size="sm" variant="glass" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <p className="mb-3 text-[11px] text-muted-foreground">
        Open findings only. A file holding three data types is counted under each of them.
        {showHeatmap ? ' Click a business unit to see its laptops.' : ' Click a business unit or a laptop to open it.'}
      </p>

      {error && <ErrorState title="Could not load laptop lineage" detail={error} onRetry={() => void load()} />}
      {!error && loading && !current && <LoadingState caption="Adding up where data types sit..." />}
      {!error && empty && (
        <div className="glass-lite rounded-md p-6 text-center text-xs text-muted-foreground">
          No open findings on laptops in this view.
        </div>
      )}

      {!error && showHeatmap && heatmap && heatmap.rows.length > 0 && (
        <>
          <HeatmapView heatmap={heatmap} onDrill={drill} />
          {(heatmap.omitted.bus > 0 || heatmap.omitted.types > 0) && (
            <div className="mt-2 text-[11px] text-muted-foreground">
              Showing the largest {plural(heatmap.rows.length - (heatmap.omitted.bus > 0 ? 1 : 0), 'business unit')} and{' '}
              {plural(heatmap.columns.length - (heatmap.omitted.types > 0 ? 1 : 0), 'data type')}; the rest are added up in the last row
              and column.
            </div>
          )}
          <FreshnessNote freshness={heatmap.freshness} className="mt-2" />
        </>
      )}

      {!error && !showHeatmap && graph && graph.nodes.length > 0 && (
        <>
          <LineageGraphView graph={graph} onDrill={drill} />
          {graph.truncated && (
            <div className="mt-2 text-[11px] text-muted-foreground">
              Kept to {graph.maxNodes} boxes at most:
              {graph.omitted.bus > 0 ? ` ${plural(graph.omitted.bus, 'business unit')},` : ''}
              {graph.omitted.devices > 0 ? ` ${plural(graph.omitted.devices, 'laptop')},` : ''}
              {graph.omitted.types > 0 ? ` ${plural(graph.omitted.types, 'data type')},` : ''}
              {graph.omitted.folders > 0 ? ` ${plural(graph.omitted.folders, 'folder')}` : ''} are added up in the dashed boxes.
            </div>
          )}
          <FreshnessNote freshness={graph.freshness} className="mt-2" />
        </>
      )}
    </GlassCard>
  );
}
